import {Box, Typography} from '@mui/material';
import {useCallback, useEffect, useRef} from 'react';
import './ExitProtocolModal.scss';
import {t} from 'i18next';
import ArrModal from '../../../../../../common/arr-modal/ArrModal';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../../../../common/arr-button/ArrButton';
import {useDispatch, useSelector} from 'react-redux';
import {resetProtocol} from '../../../../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../../../../redux/store';
import {areProtocolFormsEqual} from '../../../../protocolIntelligenceV2/utils/protocolVersionPayload';
import {navigateToPendingOrProtocolTab} from './protocolExitNavigation';

interface ExitProtocolModalProps {
  open: boolean;
  onClose: () => void;
  pendingPath?: string | null;
  /** When set, Discard switches to this version instead of leaving the protocol. */
  pendingSwitchVersionId?: number | null;
  /** When set (e.g. version switch), replaces default discard+navigate behavior. */
  onDiscard?: (versionId?: number | null) => void;
  /** When set (e.g. version switch), replaces default save-draft+navigate behavior. */
  onSaveDraft?: () => void;
  canSaveDraft?: boolean;
  isSaving?: boolean;
}

const ExitProtocolModal = ({
  open,
  onClose,
  pendingPath = null,
  pendingSwitchVersionId = null,
  onDiscard,
  onSaveDraft,
  canSaveDraft,
  isSaving = false,
}: ExitProtocolModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const protocol = useSelector((state: RootState) => state.aiReviewV2.protocol);
  const initialProtocol = useSelector(
    (state: RootState) => state.aiReviewV2.initialProtocol
  );
  const hasUnsavedChanges = !areProtocolFormsEqual(protocol, initialProtocol);
  const shouldShowModal = open && hasUnsavedChanges;
  const isNavLeave = Boolean(pendingPath);

  // Latch the version id for as long as this confirmation is open. Ghost
  // outside-clicks can clear parent state before Discard; without this latch
  // Discard falls through to protocol-list navigation (AVR-22636).
  const versionSwitchIdRef = useRef<number | null>(null);
  if (open && pendingSwitchVersionId != null) {
    versionSwitchIdRef.current = pendingSwitchVersionId;
  }
  if (!open) {
    versionSwitchIdRef.current = null;
  }
  const switchVersionId = pendingSwitchVersionId ?? versionSwitchIdRef.current;
  const isVersionSwitch = switchVersionId != null;

  const leaveAfterDiscard = useCallback(() => {
    if (isVersionSwitch) {
      onDiscard?.(switchVersionId);
      return;
    }
    dispatch(resetProtocol());
    navigateToPendingOrProtocolTab(navigate, pendingPath);
    onClose();
  }, [
    dispatch,
    isVersionSwitch,
    navigate,
    onClose,
    onDiscard,
    pendingPath,
    switchVersionId,
  ]);

  useEffect(() => {
    if (!open || hasUnsavedChanges) {
      return;
    }
    leaveAfterDiscard();
  }, [hasUnsavedChanges, leaveAfterDiscard, open]);

  const handleDraft = () => {
    if (!onSaveDraft) {
      return;
    }
    onSaveDraft();
  };

  const isDraftDisabled =
    canSaveDraft === undefined
      ? !protocol?.protocolName?.trim() || isSaving
      : !canSaveDraft || isSaving;

  const descriptionKey = isNavLeave
    ? 'ai.review.protocolIntelligence.exitProtocolModalNavDescription'
    : 'ai.review.protocolIntelligence.exitProtocolModalDescription';

  return (
    <ArrModal
      className="exitprotocoldetail"
      showModal={shouldShowModal}
      onClose={onClose}
      showClose={false}
      disabledClose={true}
      disableEscapeKeyDown={true}
      title={t('ai.review.protocolIntelligence.exitProtocolModalTitle')}
      aria-label={t('ai.review.protocolIntelligence.exitProtocolModalTitle')}
      dataTestId="arr-review-exit-protocol-detail-modal"
      buttons={[
        {
          id: 'draft-btn',
          label: t('ai.review.protocolIntelligence.exitProtocolModalDraftBtn'),
          onClick: handleDraft,
          disabled: isDraftDisabled,
          loading: isSaving,
          variant: 'text',
          dataTestId: 'epd-draft-button',
          className: 'draft-button',
        },
      ]}
    >
      <Typography
        className="exitprotocoldetail-description"
        data-testid="exit-protocol-modal-description"
      >
        {t(descriptionKey)}
      </Typography>
      <Box className="exit-modal-btn-container">
        <ArrButton
          variant="secondary"
          size="large"
          disabled={false}
          aria-label={t(
            'ai.review.protocolIntelligence.exitProtocolModalDiscardBtn'
          )}
          dataTestId="discard-btn"
          className="discard-btn"
          onClick={leaveAfterDiscard}
        >
          {t('ai.review.protocolIntelligence.exitProtocolModalDiscardBtn')}
        </ArrButton>
        <ArrButton
          variant="primary"
          size="large"
          disabled={false}
          aria-label={t(
            'ai.review.protocolIntelligence.exitProtocolModalEditBtn'
          )}
          dataTestId="edit-btn"
          className="edit-btn"
          onClick={onClose}
        >
          {t('ai.review.protocolIntelligence.exitProtocolModalEditBtn')}
        </ArrButton>
      </Box>
    </ArrModal>
  );
};

export default ExitProtocolModal;
